/**
 * Debug Overlay for Poker Reborn
 *
 * On-screen debug log overlay for development and testing.
 * Shows timestamped messages in a green-on-black overlay.
 *
 * Enable via URL parameter: ?debug=true
 *
 * Usage:
 * ```typescript
 * import { debugOverlay } from '@/utils/DebugOverlay';
 *
 * debugOverlay.log('Something happened');
 * debugOverlay.log('Value is', someValue);
 * ```
 *
 * @see docs/CONSOLE_LOGGING.md - Logging conventions
 */

// =============================================================================
// TYPES
// =============================================================================

interface DebugOverlayConfig {
  /** Maximum number of lines to keep in the overlay */
  maxLines: number;
  /** Position on screen */
  position: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  /** Overlay width in pixels */
  width: number;
  /** Overlay max height in pixels */
  maxHeight: number;
  /** Font size in pixels */
  fontSize: number;
  /** Background opacity (0-1) */
  backgroundOpacity: number;
}

// =============================================================================
// DEFAULT CONFIG
// =============================================================================

const DEFAULT_CONFIG: DebugOverlayConfig = {
  maxLines: 100,
  position: 'top-left',
  width: 420,
  maxHeight: 500,
  fontSize: 10,
  backgroundOpacity: 0.85,
};

// =============================================================================
// DEBUG OVERLAY CLASS
// =============================================================================

/**
 * On-screen debug overlay for development.
 *
 * Singleton class that manages a DOM overlay for showing debug messages.
 * Only active when ?debug=true is in the URL.
 */
class DebugOverlay {
  private static _instance: DebugOverlay | null = null;

  private _enabled: boolean = false;
  private _overlay: HTMLDivElement | null = null;
  private _content: HTMLDivElement | null = null;
  private _config: DebugOverlayConfig = DEFAULT_CONFIG;
  private _lines: string[] = [];
  private _minimized: boolean = false;
  private _minimizeButton: HTMLButtonElement | null = null;

  // ---------------------------------------------------------------------------
  // Constructor & Singleton
  // ---------------------------------------------------------------------------

  private constructor() {
    this._enabled = this.checkUrlParam();
    if (this._enabled) {
      // Defer overlay creation until DOM is ready
      if (typeof document !== 'undefined') {
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => { this.createOverlay(); });
        } else {
          this.createOverlay();
        }
      }
    }
  }

  /**
   * Get the singleton instance.
   */
  static getInstance(): DebugOverlay {
    if (!DebugOverlay._instance) {
      DebugOverlay._instance = new DebugOverlay();
    }
    return DebugOverlay._instance;
  }

  /**
   * Reset the singleton instance (for testing).
   */
  static resetInstance(): void {
    if (DebugOverlay._instance?._overlay) {
      DebugOverlay._instance._overlay.remove();
    }
    DebugOverlay._instance = null;
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  /**
   * Check if debug overlay is enabled.
   */
  get enabled(): boolean {
    return this._enabled;
  }

  /**
   * Log a message to the debug overlay.
   *
   * @param args - Values to log (will be joined with spaces)
   */
  log(...args: unknown[]): void {
    if (!this._enabled) return;

    // Lazy create overlay if not yet created (DOM might not have been ready)
    if (!this._overlay) {
      this.createOverlay();
    }
    if (!this._overlay) return;

    const message = args
      .map((arg) => {
        if (typeof arg === 'object') {
          try {
            return JSON.stringify(arg);
          } catch {
            return String(arg);
          }
        }
        return String(arg);
      })
      .join(' ');

    const time = new Date().toISOString().substring(11, 23);
    const line = `[${time}] ${message}`;

    // Add to front (newest first)
    this._lines.unshift(line);

    // Trim to max lines
    if (this._lines.length > this._config.maxLines) {
      this._lines = this._lines.slice(0, this._config.maxLines);
    }

    this.render();
  }

  /**
   * Clear all messages from the overlay.
   */
  clear(): void {
    this._lines = [];
    this.render();
  }

  /**
   * Manually enable the overlay (even without URL param).
   */
  enable(): void {
    if (this._enabled) return;
    this._enabled = true;
    this.createOverlay();
  }

  /**
   * Disable and hide the overlay.
   */
  disable(): void {
    this._enabled = false;
    if (this._overlay) {
      this._overlay.remove();
      this._overlay = null;
    }
  }

  /**
   * Toggle overlay visibility.
   */
  toggle(): void {
    if (this._enabled) {
      this.disable();
    } else {
      this.enable();
    }
  }

  /**
   * Toggle minimize/maximize state.
   */
  toggleMinimize(): void {
    this._minimized = !this._minimized;

    if (this._content) {
      this._content.style.display = this._minimized ? 'none' : 'block';
    }

    if (this._minimizeButton) {
      this._minimizeButton.textContent = this._minimized ? '+' : '−';
    }
  }

  /**
   * Update configuration.
   */
  configure(config: Partial<DebugOverlayConfig>): void {
    this._config = { ...this._config, ...config };
    if (this._overlay) {
      this.applyStyles();
    }
  }

  // ---------------------------------------------------------------------------
  // Private Methods
  // ---------------------------------------------------------------------------

  /**
   * Check URL for debug parameter.
   */
  private checkUrlParam(): boolean {
    if (typeof window === 'undefined') return false;
    const params = new URLSearchParams(window.location.search);
    const isDebug = params.get('debug') === 'true';
    if (isDebug) {
      console.log('[DebugOverlay] debug=true detected in URL');
    }
    return isDebug;
  }

  /**
   * Create the overlay DOM element.
   */
  private createOverlay(): void {
    if (typeof document === 'undefined') return;
    if (this._overlay) return;
    if (!document.body) return; // Body not ready yet

    // Create main container
    this._overlay = document.createElement('div');
    this._overlay.id = 'debug-overlay';

    // Create header with clear button
    const header = document.createElement('div');
    header.id = 'debug-overlay-header';
    header.style.cssText = `
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 6px;
      padding-bottom: 4px;
      border-bottom: 1px solid #00ff00;
    `;

    const title = document.createElement('span');
    title.textContent = 'Debug Overlay';
    title.style.cssText = 'font-weight: bold; font-size: 11px; opacity: 0.7;';

    // Button container for header buttons
    const buttonContainer = document.createElement('div');
    buttonContainer.style.cssText = 'display: flex; gap: 4px;';

    const clearButton = document.createElement('button');
    clearButton.textContent = 'Clear';
    clearButton.style.cssText = `
      background: #333;
      color: #00ff00;
      border: 1px solid #00ff00;
      border-radius: 3px;
      padding: 2px 8px;
      font-size: 10px;
      font-family: inherit;
      cursor: pointer;
      pointer-events: auto;
    `;
    clearButton.addEventListener('click', () => { this.clear(); });
    clearButton.addEventListener('mouseenter', () => {
      clearButton.style.background = '#00ff00';
      clearButton.style.color = '#000';
    });
    clearButton.addEventListener('mouseleave', () => {
      clearButton.style.background = '#333';
      clearButton.style.color = '#00ff00';
    });

    // Minimize/Maximize button
    this._minimizeButton = document.createElement('button');
    this._minimizeButton.textContent = '−';
    this._minimizeButton.style.cssText = `
      background: #333;
      color: #00ff00;
      border: 1px solid #00ff00;
      border-radius: 3px;
      padding: 2px 8px;
      font-size: 14px;
      font-family: inherit;
      cursor: pointer;
      pointer-events: auto;
      font-weight: bold;
    `;
    this._minimizeButton.addEventListener('click', () => { this.toggleMinimize(); });
    this._minimizeButton.addEventListener('mouseenter', () => {
      if (this._minimizeButton) {
        this._minimizeButton.style.background = '#00ff00';
        this._minimizeButton.style.color = '#000';
      }
    });
    this._minimizeButton.addEventListener('mouseleave', () => {
      if (this._minimizeButton) {
        this._minimizeButton.style.background = '#333';
        this._minimizeButton.style.color = '#00ff00';
      }
    });

    buttonContainer.appendChild(clearButton);
    buttonContainer.appendChild(this._minimizeButton);

    header.appendChild(title);
    header.appendChild(buttonContainer);

    // Create content area for log messages
    this._content = document.createElement('div');
    this._content.id = 'debug-overlay-content';

    this._overlay.appendChild(header);
    this._overlay.appendChild(this._content);
    this.applyStyles();
    document.body.appendChild(this._overlay);

    // Log to console for debugging
    console.log('[DebugOverlay] Overlay created and attached to body');

    // Log initial message to overlay
    this.log('Debug overlay enabled');
  }

  /**
   * Apply styles to the overlay.
   */
  private applyStyles(): void {
    if (!this._overlay) return;

    const { position, width, maxHeight, fontSize, backgroundOpacity } = this._config;

    // Position styles
    const positionStyles: Record<string, string> = {
      'top-left': 'top:10px;left:10px;',
      'top-right': 'top:10px;right:10px;',
      'bottom-left': 'bottom:10px;left:10px;',
      'bottom-right': 'bottom:10px;right:10px;',
    };

    this._overlay.style.cssText = `
      position: fixed;
      ${positionStyles[position]}
      width: ${width}px;
      max-height: ${maxHeight}px;
      overflow-y: auto;
      background: rgba(0, 0, 0, ${backgroundOpacity});
      color: #00ff00;
      font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
      font-size: ${fontSize}px;
      line-height: 1.4;
      padding: 8px 10px;
      border-radius: 4px;
      z-index: 999999;
      pointer-events: auto;
      white-space: pre-wrap;
      word-break: break-all;
    `;
  }

  /**
   * Render the current lines to the overlay.
   */
  private render(): void {
    if (!this._content) return;
    this._content.innerHTML = this._lines
      .map((line) => this.colorize(this.escapeHtml(line)))
      .join('<br>');
  }

  /**
   * Apply color to line based on content.
   */
  private colorize(line: string): string {
    // Sequence start - bright cyan
    if (line.includes('SEQUENCE START')) {
      return `<span style="color: #00ffff; font-weight: bold;">▶ ${line}</span>`;
    }
    // Sequence end - bright magenta
    if (line.includes('SEQUENCE END')) {
      return `<span style="color: #ff00ff; font-weight: bold;">◼ ${line}</span>`;
    }
    // Step START - yellow
    if (line.includes(' START')) {
      return `<span style="color: #ffff00;">${line}</span>`;
    }
    // Step END - dim green
    if (line.includes(' END')) {
      return `<span style="color: #88ff88;">${line}</span>`;
    }
    // Default green
    return line;
  }

  /**
   * Escape HTML special characters.
   */
  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

// =============================================================================
// SINGLETON EXPORT
// =============================================================================

/**
 * Global debug overlay instance.
 *
 * Enable via URL: ?debug=true
 *
 * Usage:
 * ```typescript
 * debugOverlay.log('Message', value);
 * ```
 */
export const debugOverlay = DebugOverlay.getInstance();

// Also export class for testing
export { DebugOverlay };
